import { useCallback, useEffect, useMemo, useState } from 'react';
import { indexedDbRunRepository } from '../persistence/runRepository';
import type { NonogramPuzzle } from '../games/nonogram/types';
import { loadSettings, saveSettings } from '../persistence/settings';
import { migrateRun } from '../run/serialization';
import type { AppSettings, RunState } from '../run/types';
import { useRunController } from '../run/useRunController';
import { HomeScreen } from './HomeScreen';
import { ResultsScreen } from './ResultsScreen';
import { RunScreen } from './RunScreen';
import { SettingsScreen } from './SettingsScreen';

type Screen = 'boot' | 'home' | 'settings' | 'run' | 'saved-result' | 'recovery';

export function App() {
  const repository = useMemo(() => indexedDbRunRepository, []);
  const controller = useRunController(repository);
  const [screen, setScreen] = useState<Screen>('boot');
  const [settings, setSettings] = useState<AppSettings>(loadSettings);
  const [resumable, setResumable] = useState<RunState | null>(null);
  const [history, setHistory] = useState<RunState[]>([]);
  const [savedResult, setSavedResult] = useState<RunState | null>(null);
  const [recoveryError, setRecoveryError] = useState<string | null>(null);

  const refreshSavedData = useCallback(async () => {
    try {
      const [active, completed] = await Promise.all([
        repository.loadActive(),
        repository.listCompleted(),
      ]);
      setResumable(active ? migrateRun(active) : null);
      setHistory(completed.map(migrateRun));
    } catch (error) {
      setRecoveryError(error instanceof Error ? error.message : 'Local data could not be read.');
      setScreen('recovery');
      return;
    }
    setScreen('home');
  }, [repository]);

  useEffect(() => {
    void refreshSavedData();
  }, [refreshSavedData]);

  useEffect(() => {
    saveSettings(settings);
    document.documentElement.dataset.reduceMotion = String(settings.reducedMotion);
  }, [settings]);

  const goHome = () => {
    setSavedResult(null);
    void refreshSavedData();
  };

  const startRun = (seed: string, explicitReplay = false) => {
    const recentNonograms = explicitReplay
      ? []
      : history.flatMap((run) =>
          run.puzzles.flatMap((puzzle) =>
            puzzle.gameId === 'nonogram' ? [puzzle.payload as NonogramPuzzle] : [],
          ),
        );
    controller.start(seed, settings.mode, recentNonograms);
    setResumable(null);
    setScreen('run');
  };

  const abandonRun = async () => {
    await controller.abandon();
    await refreshSavedData();
  };

  const restartRun = async (seed: string) => {
    await controller.abandon();
    startRun(seed, true);
  };

  if (screen === 'boot') {
    return (
      <main className="center-screen">
        <span className="wordmark loading-wordmark">GAUNTLE</span>
      </main>
    );
  }

  if (screen === 'recovery') {
    return (
      <main className="center-screen">
        <section className="panel error-panel" role="alert">
          <p className="eyebrow">Local data recovery</p>
          <h1>Your saved data could not be opened.</h1>
          <p>{recoveryError}</p>
          <button
            className="button button--danger"
            onClick={async () => {
              await repository.deleteAll();
              setRecoveryError(null);
              await refreshSavedData();
            }}
          >
            Clear damaged local data
          </button>
        </section>
      </main>
    );
  }

  if (screen === 'settings') {
    return (
      <SettingsScreen
        settings={settings}
        onChange={setSettings}
        onBack={() => setScreen('home')}
        onClearData={async () => {
          await repository.deleteAll();
          setResumable(null);
          setHistory([]);
          setScreen('home');
        }}
      />
    );
  }

  if (screen === 'saved-result' && savedResult) {
    return (
      <ResultsScreen run={savedResult} onHome={goHome} onReplay={(seed) => startRun(seed, true)} />
    );
  }

  if (screen === 'run') {
    return (
      <RunScreen
        controller={controller}
        settings={settings}
        onHome={goHome}
        onReplay={(seed) => startRun(seed, true)}
        onAbandon={() => void abandonRun()}
        onRestart={(seed) => void restartRun(seed)}
      />
    );
  }

  return (
    <HomeScreen
      settings={settings}
      resumable={resumable}
      history={history}
      onStart={startRun}
      onResume={() => {
        if (!resumable) return;
        controller.resume(resumable);
        setScreen('run');
      }}
      onSettings={() => setScreen('settings')}
      onOpenResult={(run) => {
        setSavedResult(run);
        setScreen('saved-result');
      }}
    />
  );
}
